import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Dialog, DialogContent, Grid, IconButton, Typography, Tooltip, Link } from "@mui/material";
import { lighten, useTheme } from "@mui/material/styles";
import { EmployeeRef } from "../../../../models/employees";
import DialogTitle from "../../../DialogTitle/DialogTitle";
import { MessageIcon, MobileCheck, RoundedListIcon } from "../../../../assets/icons";
import { EmployeeActivityApiService } from "../../../../services/employee-activity/employee-activity-api.service";
import { EmployeeActivityFilter } from "../../../../models/employee-activity/employee-activity-filter";
import { GridColumns, GridSortItem, GridSortModel, GridValidRowModel } from "@mui/x-data-grid";
import { getOnlyTime, intlFormatDateShort, intlFormatDateToHour } from "../../../../utils/time-utils";
import ConcatenatedCell from "../../../../reusable-components/ConcatenatedCell/ConcatenatedCell";
import { intlFormatDateWithHour } from "../../../../utils/time-utils";
import SimplePopover from "../../../../reusable-components/SimplePopover/SimplePopover";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleCheck,
  faCircleExclamation,
  faEllipsisH,
  faSearchLocation,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import { FT_TOAN } from "../../../../utils/constants/filter-tables-constants";
import FlagIcon from "../../../FlagIcon/FlagIcon";
import { differenceInSeconds, isAfter } from "date-fns";
import { AboutItem, UserActivityHistoryDTO } from "../../../../models/user-activity-history";
import { NotificationRef } from "../../../../models/notifications/notification-ref";
import useWindowSize from "../../../../custom-hooks/useWindowSize/useWindowSize";
import useSearchOptions from "../../../../custom-hooks/useSearchOptions/useSearchOptions";
import { Page } from "../../../../models/api/page";
import { toastifyResponse } from "../../../../utils/toastify-response/toastify-response";
import { UserEmployeeActivityHistoryDTO } from "../../../../models/user-employee-activity-history";
import TableHeader from "../../../../containers/TableHeader/TableHeader";
import { SPACING } from "../../../../_globalStore/redux/reducers/Theme/themeConstants";
import SearchTableMessage from "../../../../reusable-components/SearchTableMessage/SearchTableMessage";
import LoadingOverlay from "../../../../containers/LoadingOverlay/LoadingOverlay";
import { DataGrid } from "../../../../reusable-components/Table/styles";
import { isEqual } from "lodash";
import { ListSortDirection } from "../../../../models/api/sort";
import { USER_ACTIVITY_ACTIONS } from "../../../../utils/constants/constants";
import { stringToLowerCase } from "./utils";
import { ViaPlatformQuickfilter, useViaPlatformQuickFilter } from "src/containers/Quickfilters/ViaPlatformQuickfilter";
import { APP_MOBILE_APP_PREFIX } from "../../../../utils/constants/constants";
interface Props {
  employee: EmployeeRef;
}
interface State {
  isOpen: boolean;
  loading: boolean;
  items: UserEmployeeActivityHistoryDTO[];
  totalItems: number;
  error: string;
  page: Page;
}
const EmployeeNotificationsAndActivity = ({ employee }: Props) => {
  const { t } = useTranslation();
  const tRP = "components.team_presence_and_leaves_dashboard.employee_notification_and_activity";

  const theme = useTheme();
  const { height } = useWindowSize();

  const { searchOptions, handleChangeSearchOptions } = useSearchOptions(FT_TOAN);

  const [state, setState] = useState<State>({
    isOpen: false,
    loading: false,
    items: [],
    totalItems: 0,
    error: "",
    page: {
      pageNumber: 0,
      pageSize: 10,
    },
  });

  const { viaPlatformQuickFilter, setViaPlatformQuickFilter, selectedViaPlatformValue } = useViaPlatformQuickFilter();

  const [sortModel, setSortModel] = useState<GridSortItem[]>([
    {
      field: new EmployeeActivityApiService().defaultSort[0].name,
      sort: new EmployeeActivityApiService().defaultSort[0].direction === 0 ? "asc" : "desc",
    },
  ]);

  const handleChangeSortModel = (model: GridSortModel) => {
    if (!isEqual(sortModel, model) && model.length) setSortModel(model);
  };

  const handleChangePage = (param: keyof Page, value: number) =>
    setState((state) => ({
      ...state,
      page: {
        pageNumber: param === "pageNumber" ? value : 0,
        pageSize: param === "pageSize" ? value : state.page.pageSize,
      },
    }));

  const loadData = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      if (employee?.id) {
        setState((state) => ({
          ...state,
          loading: true,
        }));

        const { field, sort } = sortModel[0];
        const { Ascending, Descending } = ListSortDirection;

        try {
          const response = await new EmployeeActivityApiService().loadItems(
            { employeeId: employee.id, ...selectedViaPlatformValue } as EmployeeActivityFilter,
            searchOptions.query,
            [
              {
                name: field,
                direction: sort === "asc" ? Ascending : Descending,
              },
            ],
            state.page,
            searchOptions.exactMatch,
            60,
            signal
          );

          if (!signal?.aborted) {
            if (!response.error) {
              setState((state) => ({
                ...state,
                items: response.items,
                totalItems: response.totalItems,
                error: "",
              }));
            } else if (!signal?.aborted) {
              setState((state) => ({
                ...state,
                items: [],
                totalItems: 0,
                error: response.error,
              }));
            }
          }

          toastifyResponse(response, "failed", undefined, "failure_fetching_data", "error_fetching_data");
        } catch (error) {
          if (!signal?.aborted) {
            setState((state) => ({
              ...state,
              error: error as string,
            }));
          }
        } finally {
          setState((state) => ({
            ...state,
            loading: false,
          }));
        }
      }
    },
    [searchOptions, state.page, employee?.id, sortModel, selectedViaPlatformValue]
  );

  //#region reload data when page changes
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const abortController = new AbortController();

    if (state.isOpen) {
      searchOptions.query.length === 0
        ? loadData(abortController.signal)
        : (timer = setTimeout(() => loadData(abortController.signal), searchOptions.query.length === 0 ? 0 : 1000));
    }

    return () => {
      clearTimeout(timer);
      abortController.abort();
    };
  }, [state.page]);
  //#endregion

  //#region set pageNumber to 0 upon changes in searchOptions / via platform filter / employee id
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const abortController = new AbortController();

    if (state.page.pageNumber === 0) {
      if (state.isOpen) {
        searchOptions.query.length === 0
          ? loadData(abortController.signal)
          : (timer = setTimeout(() => loadData(abortController.signal), searchOptions.query.length === 0 ? 0 : 1000));
      }

      return () => {
        clearTimeout(timer);
        abortController.abort();
      };
    } else {
      timer = setTimeout(
        () => setState((state) => ({ ...state, page: { ...state.page, pageNumber: 0 } })),
        searchOptions.query.length === 0 ? 0 : 1000
      );
    }
  }, [searchOptions, selectedViaPlatformValue, employee?.id, state.isOpen]);
  //#endregion

  const columns: GridColumns<UserActivityHistoryDTO> = [
    {
      field: "createdAt",
      headerAlign: "center",
      headerName: `${t(`${tRP}.fields.date`)}`,
      flex: 0.5,
      maxWidth: 120,
      renderCell: ({ row }) => (
        <ConcatenatedCell
          aligned="left"
          FirstElement={
            <Tooltip title={row.createdByUser?.email}>
              <Typography>{intlFormatDateShort(row.createdAt)}</Typography>
            </Tooltip>
          }
          SecondElement={
            <Typography title={intlFormatDateToHour(row.createdAt)}>{intlFormatDateToHour(row.createdAt)}</Typography>
          }
        />
      ),
    },
    {
      field: "action",
      flex: 1,
      headerAlign: "center",
      headerName: `${t(`${tRP}.fields.action`)}`,
      renderCell: ({ row }) => (
        <Grid style={{ display: "flex", alignItems: "center", flexWrap: "wrap", width: "100%" }}>
          <Typography
            style={{ whiteSpace: "break-spaces" }}
            title={row.aboutItems
              .map((i: AboutItem) => {
                return i.kind + ": " + i.data;
              })
              .join(", ")}
          >
            <span style={{ color: theme.palette.primary.main }}>{`${row.createdByUser?.name}`}</span> &nbsp;
            {`${stringToLowerCase(row.userActivityAction?.name)} ${
              row.aboutItems[0]?.details
                ? parseFloat(row.aboutItems[0]?.details) &&
                  row.userActivityAction &&
                  (row.userActivityAction.id === USER_ACTIVITY_ACTIONS.RequestedExtraHours.id ||
                    row.userActivityAction.id === USER_ACTIVITY_ACTIONS.ApprovedExtraHoursAsTeamManager.id ||
                    row.userActivityAction.id === USER_ACTIVITY_ACTIONS.RejectedExtraHoursAsTeamManager.id ||
                    row.userActivityAction.id === USER_ACTIVITY_ACTIONS.ApprovedExtraHoursByLinkAsTeamManager.id ||
                    row.userActivityAction.id === USER_ACTIVITY_ACTIONS.RejectedExtraHoursByLinkAsTeamManager.id ||
                    row.userActivityAction.id === USER_ACTIVITY_ACTIONS.EnteredBillableTime.id ||
                    row.userActivityAction.id === USER_ACTIVITY_ACTIONS.CancelledExtraHours.id ||
                    row.userActivityAction.id ===
                      USER_ACTIVITY_ACTIONS.AcceptedExtraHoursByLinkAsClientTeamManager.id ||
                    row.userActivityAction.id ===
                      USER_ACTIVITY_ACTIONS.RejectedExtraHoursByLinkAsClientTeamManager.id ||
                    row.userActivityAction.id === USER_ACTIVITY_ACTIONS.AcceptedExtraHoursAsClientTeamManager.id ||
                    row.userActivityAction.id === USER_ACTIVITY_ACTIONS.RejectedExtraHoursAsClientTeamManager.id)
                  ? `(${row.aboutItems
                      .reduce((acc: number, curr: AboutItem) => acc + parseFloat(curr.details), 0)
                      .toFixed(2)} h)`
                  : `(${row.aboutItems[0]?.details})`
                : ""
            }`}
          </Typography>
          &nbsp;
          {row.responseError && (
            <FontAwesomeIcon
              style={{ color: theme.palette.error.main }}
              icon={faCircleExclamation}
              title={row.responseError}
            />
          )}
        </Grid>
      ),
    },
    {
      field: "notifications",
      headerName: `${t(`${tRP}.fields.notifications`)}`,
      flex: 1,
      sortable: false,
      headerAlign: "center",
      renderCell: ({ row }) => (
        <Grid style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
          <Grid>
            {row.notifications.slice(0, 2).map((notification: NotificationRef) => {
              const isPending = isAfter(
                new Date(new Date(row.createdAt).getTime() + (notification?.eventType?.defer as number) * 1000),
                new Date()
              );

              const delay = differenceInSeconds(
                new Date(new Date(row.createdAt).getTime() + (notification?.eventType?.defer as number) * 1000),
                new Date()
              );

              return (
                <Grid
                  style={{ display: "flex", gap: "5px" }}
                  key={notification.id}
                >
                  {notification?.forUser?.name}{" "}
                  {isPending ? (
                    `${t(`${tRP}.will_be_notified_in`).replace(
                      "${numberOfMinutes}",
                      (Math.abs(delay) / 60).toFixed(2)
                    )}`
                  ) : // `will be notitfied in ${(Math.abs(delay) / 60).toFixed(2)} minute(s) `
                  notification.isDelivered ? (
                    `${t(`${tRP}.was_notified`)}`
                  ) : !notification.error ? (
                    <Trans
                      i18nKey={`${t(`${tRP}.not_notified_yet`)}`}
                      components={{ bold: <b /> }}
                    />
                  ) : (
                    <Trans
                      i18nKey={`${t(`${tRP}.was_not_notified`)}`}
                      components={{ bold: <b /> }}
                    />
                  )}
                  <SimplePopover
                    buttonType="icon"
                    buttonContent={<MessageIcon style={{ color: lighten(theme.palette.primary.main, 0.5) }} />}
                    customButtonStyle={{ padding: "0px", minWidth: "unset", width: "20px" }}
                  >
                    <Grid style={{ padding: "10px", maxWidth: "60vw" }}>
                      <Typography>
                        <b>{`${t(`${tRP}.from`)}`} :</b> {`${t(`${tRP}.generated_by_system`)}`}
                      </Typography>
                      <Typography>
                        <b>{`${t(`${tRP}.to`)}`} :</b> {notification.forUser?.name}
                      </Typography>
                      <Typography>
                        <b>{`${t(`${tRP}.subject`)}`} :</b> {notification.subject}
                      </Typography>
                      <br />
                      <pre dangerouslySetInnerHTML={{ __html: notification.message }}></pre>
                      <br />
                      <>
                        {isPending ? (
                          `${t(`${tRP}.notification_will_be_sent_in`).replace(
                            "${numberOfMinutes}",
                            (Math.abs(delay) / 60).toFixed(2)
                          )}`
                        ) : notification.isDelivered ? (
                          <Typography style={{ color: theme.palette.success.main }}>
                            {`${t(`${tRP}.successfully_delivered_message`).replace(
                              "${date}",
                              intlFormatDateWithHour(
                                new Date(
                                  new Date(row.createdAt).getTime() + (notification?.eventType?.defer as number) * 1000
                                )
                              )
                            )}`}
                            <FontAwesomeIcon
                              icon={faCircleCheck}
                              style={{ color: theme.palette.success.main }}
                            />
                          </Typography>
                        ) : !notification.error ? (
                          <Typography
                            style={{ color: theme.palette.warning.main }}
                            title={notification.error}
                          >
                            {`${t(`${tRP}.not_sent_yet`)}`}
                          </Typography>
                        ) : (
                          <Typography style={{ color: theme.palette.error.main }}>
                            {`${t(`${tRP}.not_delivered_message`)}`}
                            <FontAwesomeIcon
                              icon={faTriangleExclamation}
                              style={{ color: theme.palette.error.light }}
                            />
                          </Typography>
                        )}
                      </>
                    </Grid>
                  </SimplePopover>
                </Grid>
              );
            })}
          </Grid>
          {row.notifications.length > 2 && (
            <SimplePopover
              buttonType="icon"
              buttonContent={
                <>
                  <FontAwesomeIcon
                    icon={faEllipsisH}
                    title={"See more notifications"}
                  />
                  {`+${row.notifications.length - 2} `}
                </>
              }
            >
              {row.notifications.slice(2).map((notification: NotificationRef) => {
                const isPending = isAfter(
                  new Date(new Date(row.createdAt).getTime() + (notification?.eventType?.defer as number) * 1000),
                  new Date()
                );

                const delay = differenceInSeconds(
                  new Date(new Date(row.createdAt).getTime() + (notification?.eventType?.defer as number) * 1000),
                  new Date()
                );

                return (
                  <Grid
                    style={{ display: "flex", padding: "10px" }}
                    key={notification.id}
                  >
                    {notification.forUser?.name}{" "}
                    {isPending ? (
                      `${t(`${tRP}.will_be_notified_in`).replace(
                        "${numberOfMinutes}",
                        (Math.abs(delay) / 60).toFixed(2)
                      )}`
                    ) : notification.isDelivered ? (
                      `was notified`
                    ) : !notification.error ? (
                      <Typography>
                        is <b>not </b> notified yet
                      </Typography>
                    ) : (
                      <Typography>
                        {" "}
                        was <b>not </b> notified
                      </Typography>
                    )}
                    <SimplePopover
                      buttonType="icon"
                      buttonContent={<MessageIcon style={{ color: lighten(theme.palette.primary.main, 0.5) }} />}
                      customButtonStyle={{ padding: "0px", minWidth: "unset", width: "20px" }}
                    >
                      <Grid style={{ padding: "10px", maxWidth: "60vw" }}>
                        <Typography>
                          <b>{`${t(`${tRP}.from`)}`} :</b> {`${t(`${tRP}.generated_by_system`)}`}
                        </Typography>
                        <Typography>
                          <b>{`${t(`${tRP}.to`)}`} :</b> {notification.forUser?.name}
                        </Typography>
                        <Typography>
                          <b>{`${t(`${tRP}.subject`)}`} :</b> {notification.subject}
                        </Typography>
                        <br />
                        <pre dangerouslySetInnerHTML={{ __html: notification.message }}></pre>
                        <br />
                        <>
                          {isPending ? (
                            `${t(`${tRP}.notification_will_be_sent_in`).replace(
                              "${numberOfMinutes}",
                              (Math.abs(delay) / 60).toFixed(2)
                            )}`
                          ) : notification.isDelivered ? (
                            <Typography style={{ color: theme.palette.success.main }}>
                              {`${t(`${tRP}.successfully_delivered_message`).replace(
                                "${date}",
                                intlFormatDateWithHour(
                                  new Date(
                                    new Date(row.createdAt).getTime() +
                                      (notification?.eventType?.defer as number) * 1000
                                  )
                                )
                              )}`}
                              <FontAwesomeIcon
                                icon={faCircleCheck}
                                style={{ color: theme.palette.success.main }}
                              />
                            </Typography>
                          ) : !notification.error ? (
                            <Typography
                              style={{ color: theme.palette.warning.main }}
                              title={notification.error}
                            >
                              {`${t(`${tRP}.not_sent_yet`)}`}
                            </Typography>
                          ) : (
                            <Typography style={{ color: theme.palette.error.main }}>
                              {`${t(`${tRP}.not_delivered_message`)}`}
                              <FontAwesomeIcon
                                icon={faTriangleExclamation}
                                style={{ color: theme.palette.error.light }}
                              />
                            </Typography>
                          )}
                        </>
                      </Grid>
                    </SimplePopover>
                  </Grid>
                );
              })}
            </SimplePopover>
          )}
          {(row.userActivityAction?.id === USER_ACTIVITY_ACTIONS.LoggedIn.id ||
            row.userActivityAction?.id === USER_ACTIVITY_ACTIONS.Requested2FaCode.id ||
            row.userActivityAction?.id === USER_ACTIVITY_ACTIONS.LoggedInWith2FaCode.id) && (
            <Grid style={{ display: "flex", alignContent: "center", gap: "5px" }}>
              {row.countryCode ? (
                <Link
                  href={`https://maps.google.com/maps?q=${row.latitude},${row.longitude}&iwloc=A&hl=en`}
                  target="_blank"
                  title={"See on map"}
                >
                  <FlagIcon
                    size="xs"
                    countryCode={row.countryCode}
                  />
                </Link>
              ) : (
                <Link
                  href={`https://maps.google.com/maps?q=${row.latitude},${row.longitude}&iwloc=A&hl=en`}
                  target="_blank"
                  title={"See on map"}
                >
                  <FontAwesomeIcon
                    icon={faSearchLocation}
                    color={theme.palette.primary.contrastText}
                  />
                </Link>
              )}
              <Grid>
                <Typography
                  title={row.regionName}
                  variant="body2"
                >
                  {row.city}
                </Typography>
                <Typography variant="body2">{row.countryName}</Typography>
                <Typography variant="body2">{row.externalIP}</Typography>
              </Grid>
            </Grid>
          )}
        </Grid>
      ),
    },
    {
      field: "userAgent",
      headerName: `${t(`${tRP}.fields.performed_via`)}`,
      headerAlign: "center",
      align: "center",
      flex: 0.6,
      renderCell: ({ row }) => (
        <Grid>
          {row.userAgent?.includes(APP_MOBILE_APP_PREFIX) ? (
            <>
              <Typography>
                {row.userAgent.includes("Android")
                  ? `${t(`${tRP}.fields.android`)}`
                  : row.userAgent.includes("iPhone")
                  ? `${t(`${tRP}.fields.iphone`)}`
                  : `${t(`${tRP}.fields.another_device`)}`}
              </Typography>
            </>
          ) : (
            <Typography>{`${t(`${tRP}.fields.web`)}`}</Typography>
          )}
        </Grid>
      ),
    },
  ];

  const lastLoginAttemptUser = useMemo(() => {
    return !!state.items[0]?.employeeUsers?.length
      ? state.items[0].employeeUsers.sort((a, b) => {
          return (
            (!!a.lastLoginAttempt ? new Date(a.lastLoginAttempt).getTime() : 0) -
            (!!b.lastLoginAttempt ? new Date(b.lastLoginAttempt).getTime() : 0)
          );
        })[0]
      : null;
  }, [state.items[0]?.employeeUsers]);

  return (
    <>
      <IconButton
        onClick={() =>
          setState((state) => ({
            ...state,
            isOpen: true,
          }))
        }
        style={{ color: lighten(theme.palette.primary.main, 0.4) }}
        title={`See ${employee.name} activity and notifications`}
      >
        <RoundedListIcon />
      </IconButton>
      <Dialog
        open={state.isOpen}
        maxWidth="lg"
        fullWidth
      >
        <LoadingOverlay loading={state.loading}>
          <DialogTitle
            // title={`${employee.name} notifications and activity`}
            title={`${t(`${tRP}.dialog_title`, { employeeName: employee.name })}`}
            handleClose={() => setState((state) => ({ ...state, isOpen: false }))}
          />
          <DialogContent>
            <TableHeader
              searchOptions={searchOptions}
              tableTitle={undefined}
              handleChangeSearchOptions={handleChangeSearchOptions}
              quickFilters={
                <>
                  <ViaPlatformQuickfilter
                    quickFilters={viaPlatformQuickFilter}
                    handleChangeQuickFilters={setViaPlatformQuickFilter}
                  />
                  <Grid sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center", height: "42px" }}>
                    <Typography
                      variant={"body2"}
                      sx={{ fontWeight: "bold" }}
                    >{`${t(`${tRP}.last_login_attempt`)}: ${
                      !!lastLoginAttemptUser?.lastLoginAttempt
                        ? `${intlFormatDateShort(new Date(lastLoginAttemptUser.lastLoginAttempt))} ${getOnlyTime(
                            lastLoginAttemptUser.lastLoginAttempt
                          )}`
                        : t(`${tRP}.never`)
                    }`}</Typography>
                  </Grid>
                </>
              }
            />

            <Grid
              sx={{
                width: "100%",
                height: `calc(
              ${height}px -
              ${
                SPACING[9] * 2 +
                (document.getElementById("header")?.clientHeight ?? 0) +
                SPACING[7] +
                (document.getElementById("MultitabsHorizontal-Portal")?.clientHeight ?? 0) +
                SPACING[7] +
                (document.getElementById("tableHeader")?.clientHeight ?? 0) +
                SPACING[7] +
                5
              }px)`,
              }}
            >
              {!state.items[0]?.userActivityHistory?.length ? (
                <SearchTableMessage
                  loading={state.loading}
                  searchOptions={searchOptions}
                  error={state.error}
                />
              ) : (
                <DataGrid
                  rows={state.items[0]?.userActivityHistory ?? []}
                  columns={columns as GridColumns<GridValidRowModel>}
                  paginationMode="server"
                  pageSize={state.page.pageSize}
                  onPageSizeChange={(value) => handleChangePage("pageSize", value)}
                  rowsPerPageOptions={[2, 5, 10, 20, 50, 100]}
                  page={state.page.pageNumber}
                  onPageChange={(value) => handleChangePage("pageNumber", value)}
                  rowHeight={72}
                  rowCount={state.totalItems}
                  sortingMode="server"
                  sortingOrder={["asc", "desc"]}
                  sortModel={sortModel}
                  onSortModelChange={handleChangeSortModel}
                />
              )}
            </Grid>
          </DialogContent>
        </LoadingOverlay>
      </Dialog>
    </>
  );
};
export default EmployeeNotificationsAndActivity;
